import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "onRequestBack", "onRequestClose", "shouldShowBack", "shouldShowSecurityMessage", "subtitle", "title", "titleElementId", "bodyOpenClassName"];
import React from 'react';
import classes from '@nerdwallet/base-styles/classes';
import { printDeprecationWarning } from '@nerdwallet/deprecate';
import { useId } from '@nerdwallet/react-accessibility'; // TODO: in Currency v2, try to refactor so we don't need these pieces separate from `<Modal>`

import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import { ModalContent } from './ModalContent';
import { ModalImpl, DialogStateContext } from './ModalImpl';
import { useDeprecatedReactModalProps } from './use-deprecated-react-modal-props';
/**
 * Standard, accessible NerdWallet modal. Built on top of [reakit's `Dialog` component](https://reakit.io/docs/dialog/).
 */

export var Modal = function Modal(props) {
  var _useDeprecatedReactMo = useDeprecatedReactModalProps(props, 'Modal'),
      children = _useDeprecatedReactMo.children,
      onRequestBack = _useDeprecatedReactMo.onRequestBack,
      onRequestClose = _useDeprecatedReactMo.onRequestClose,
      shouldShowBack = _useDeprecatedReactMo.shouldShowBack,
      shouldShowSecurityMessage = _useDeprecatedReactMo.shouldShowSecurityMessage,
      subtitle = _useDeprecatedReactMo.subtitle,
      title = _useDeprecatedReactMo.title,
      titleElementIdProp = _useDeprecatedReactMo.titleElementId,
      bodyOpenClassName = _useDeprecatedReactMo.bodyOpenClassName,
      modalImplProps = _objectWithoutProperties(_useDeprecatedReactMo, _excluded);

  useDeprecatedClassNameStylePropsWarning('Modal', {
    className: modalImplProps.className,
    style: modalImplProps.style
  }); // autogenerate titleElementId if not provided

  var titleElementId = useId(titleElementIdProp);
  return /*#__PURE__*/React.createElement(ModalImpl // spread remaining props directly onto ModalImpl
  , _extends({}, modalImplProps, {
    // modal is always centered and scrollable
    isCentered: true,
    isScrollable: true // legacy support for managed modals (imagine a "Confirm Close" dialog)
    ,
    onRequestClose: onRequestClose // specifically tie-in ModalContent title
    ,
    "aria-labelledby": modalImplProps['aria-labelledby'] || titleElementId
  }), /*#__PURE__*/React.createElement(DialogStateContext.Consumer, null, function (_ref) {
    var setVisible = _ref.setVisible;
    return /*#__PURE__*/React.createElement(ModalContent, {
      className: classes('margin-vertical-5--wide'),
      "data-currency": "Modal",
      shouldShowSecurityMessage: shouldShowSecurityMessage,
      onRequestBack: onRequestBack,
      onRequestClose: onRequestClose || function () {
        return setVisible(false);
      },
      shouldShowBack: shouldShowBack,
      subtitle: subtitle,
      title: title,
      titleElementId: titleElementId
    }, children);
  }));
};
/**
 * Only here for backwards-compatibility.  That was a passthrough to: http://reactcommunity.org/react-modal/accessibility/#app-element
 * @deprecated
 */

Modal.setAppElement = function () {
  // TODO: remove in Currency v2
  printDeprecationWarning({
    message: 'Modal.setAppElement has been deprecated and is now a NOOP. Please remove the callsites from your app. It will be removed in the next major version'
  });
};

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var PropTypes = require('prop-types');

  Modal.propTypes = _objectSpread(_objectSpread({}, ModalImpl.propTypes), {}, {
    /**
     * Run after the modal's back button is clicked.
     */
    onRequestBack: PropTypes.func,

    /**
     * Flag to indicate if the encryption security message should be added to the modal.
     */
    shouldShowSecurityMessage: PropTypes.bool,

    /**
     * Indicates if the back button should be shown.
     */
    shouldShowBack: PropTypes.bool,

    /**
     * unique id for the title's dom element. If not provided, an autogenerated id will be used.
     */
    titleElementId: PropTypes.string,

    /**
     * The title text of the modal
     */
    title: PropTypes.string,

    /**
     * The subtitle text of the modal
     */
    subtitle: PropTypes.string,

    /**
     * Additional ARIA attributes. This could be extended for more support
     * @deprecated
     */
    aria: PropTypes.shape({
      labelledby: PropTypes.string
    }),

    /**
     * Duration for modal visual animations
     * @deprecated
     */
    closeTimeoutMS: PropTypes.number,

    /**
     * How the content container should be announced to screen readers
     * @deprecated
     */
    contentLabel: PropTypes.string,

    /**
     * Will be run after the modal has opened.
     * @deprecated
     */
    onAfterOpen: PropTypes.func
  });
}

Modal.defaultProps = {
  // ModalContent defaults
  onRequestBack: function onRequestBack(f) {
    return f;
  },
  shouldShowBack: false,
  shouldShowSecurityMessage: false,
  subtitle: null,
  title: null,
  titleElementId: null,
  // [DEPRECATED] - all props below this line should not be used.
  aria: undefined,
  contentLabel: undefined,
  onAfterOpen: undefined
};